import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Cross, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { apiError } from "@/lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [form, setForm] = useState({ new_password: "", confirm_password: "" });
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.new_password.length < 8) {
      setError("New password must be at least 8 characters");
      return;
    }
    if (form.new_password !== form.confirm_password) {
      setError("New passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, ...form });
      setDone(true);
    } catch (err) {
      setError(apiError(err, "This password reset link is invalid or has expired."));
    } finally {
      setLoading(false);
    }
  };

  const card = (content) => (
    <div className="flex min-h-screen items-center justify-center bg-navy grain-overlay px-4" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <span className="inline-flex h-12 w-12 items-center justify-center rounded-lg bg-gold text-navy">
            <Cross className="h-6 w-6" strokeWidth={2.5} />
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-cream">Set a New Password</h1>
          <p className="mt-1 text-sm text-cream/60">Christ Church CNI, Rajpura — Admin Panel</p>
        </div>
        <div className="rounded-lg border border-cream/10 bg-navy-light/60 p-8 backdrop-blur">{content}</div>
        <p className="mt-6 text-center text-xs text-cream/40">
          <Link to="/admin/login" className="hover:text-gold transition-colors" data-testid="reset-back-login-link">← Back to sign in</Link>
        </p>
      </div>
    </div>
  );

  if (!token) {
    return card(
      <div className="rounded-md border border-red-400/40 bg-red-500/10 px-4 py-3 text-sm text-red-200" data-testid="reset-password-invalid">
        This password reset link is invalid or has expired. Please request a new one from the sign-in page.
      </div>
    );
  }
  if (done) {
    return card(
      <div className="text-center">
        <div className="rounded-md border border-gold/40 bg-gold/10 px-4 py-3 text-sm text-cream" data-testid="reset-password-success">
          Your password has been reset. Please sign in with your new password.
        </div>
        <Link to="/admin/login" data-testid="reset-password-login-link">
          <Button className="mt-5 w-full bg-gold text-navy hover:bg-gold-dark hover:text-cream">Go to Sign In</Button>
        </Link>
      </div>
    );
  }
  return card(
    <form onSubmit={submit} className="space-y-5" data-testid="reset-password-form">
      {error && (
        <div className="rounded-md border border-red-400/40 bg-red-500/10 px-4 py-3 text-sm text-red-200" role="alert" data-testid="reset-password-error">
          {error}
        </div>
      )}
      <div>
        <Label htmlFor="rp-new" className="text-cream/80">New Password</Label>
        <Input
          id="rp-new"
          type="password"
          required
          autoComplete="new-password"
          data-testid="reset-password-new"
          value={form.new_password}
          onChange={(e) => setForm({ ...form, new_password: e.target.value })}
          className="mt-1.5 border-cream/20 bg-navy text-cream placeholder:text-cream/30"
        />
        <p className="mt-1 text-xs text-cream/40">At least 8 characters.</p>
      </div>
      <div>
        <Label htmlFor="rp-confirm" className="text-cream/80">Confirm New Password</Label>
        <Input
          id="rp-confirm"
          type="password"
          required
          autoComplete="new-password"
          data-testid="reset-password-confirm"
          value={form.confirm_password}
          onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
          className="mt-1.5 border-cream/20 bg-navy text-cream placeholder:text-cream/30"
        />
      </div>
      <Button
        type="submit"
        disabled={loading}
        className="w-full bg-gold text-navy hover:bg-gold-dark hover:text-cream transition-colors"
        data-testid="reset-password-submit"
      >
        <Lock className="mr-2 h-4 w-4" /> {loading ? "Resetting…" : "Reset Password"}
      </Button>
    </form>
  );
}
